import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { TrashSimple } from "@phosphor-icons/react"
import { graphql } from "relay-runtime"
import { useUser } from "@/apis/bff"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Button } from "@/components/ui/button"
import { DetailCheckbox } from "@/components/ui/checkbox"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FormField } from "@/components/ui/form"
import { TranslatedFieldError } from "@/components/ui/form-field-error"
import { Input } from "@/components/ui/input"
import {
  LabelAndSelectValue,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { withErrorBoundary } from "@/hoc/with-error-boundary"
import { useToast } from "@/hooks/useToast"
import { cn } from "@/lib/utils"
import { CareProviderSearch } from "@/modules/care-provider/simple-search/care-provider-search"
import { PhoneNumberInput } from "@/modules/patients/components/phoneNumber/PhoneNumberInput"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import { chNeuropsychologicDiagnosticPrescriptionDrawerFragment$key } from "@/modules/patients/detail/neuropsychologic-diagnostic-prescription/ch/__generated__/chNeuropsychologicDiagnosticPrescriptionDrawerFragment.graphql"
import { DOCUMENT_LANGUAGES } from "@/modules/patients/detail/neuropsychologic-diagnostic-prescription/ch/constants"
import {
  NeuropsychologicDiagnosticPrescription,
  NeuropsychologicDiagnosticPrescriptionBaseSchema,
  NeuropsychologicDiagnosticPrescriptionSchema,
} from "@/modules/patients/detail/neuropsychologic-diagnostic-prescription/ch/schema/neuropsychologic-diagnostic-prescription-schema"
import { useChCreateNeuropsychologicDiagnosticPrescriptionMutation } from "@/modules/patients/detail/neuropsychologic-diagnostic-prescription/ch/services/useChCreateNeuropsychologicDiagnosticPrescriptionMutation"
import { useChDeleteNeuropsychologicDiagnosticPrescriptionMutation } from "@/modules/patients/detail/neuropsychologic-diagnostic-prescription/ch/services/useChDeleteNeuropsychologicDiagnosticPrescriptionMutation"
import { useChGenerateNeuropsychologicDiagnosticPrescriptionMutation } from "@/modules/patients/detail/neuropsychologic-diagnostic-prescription/ch/services/useChGenerateNeuropsychologicDiagnosticPrescriptionMutation"
import { useChUpdateNeuropsychologicDiagnosticPrescriptionMutation } from "@/modules/patients/detail/neuropsychologic-diagnostic-prescription/ch/services/useChUpdateNeuropsychologicDiagnosticPrescriptionMutation"
import { usePatientFileContext } from "@/modules/patients/detail/store/usePatientStore"
import { useContextAppointment } from "@/modules/patients/hooks/useContextAppointment"
import { getDocumentLanguageLabel } from "@/modules/patients/hooks/useDefaultDocumentLanguage"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { Language, MedicalPrescriptionValues } from "@/types/__generated__/union-types"

const chNeuropsychologicDiagnosticPrescriptionFragment = graphql`
  fragment chNeuropsychologicDiagnosticPrescriptionDrawerFragment on ch_NeuropsychologicDiagnosticPrescription {
    id
    patientId
    progressNoteId
    createdAt
    createdBy
    signedAt
    signedBy
    language
    updatedAt
    updatedBy
    sentAt
    sentBy
    concludedAt
    concludedBy
    fileId
    fileMetadata {
      documentType
      extension
      lengthInKilobytes
    }
    medicalPrescriptions
    relationship
    name
    phoneNumber
    email
    healthcareProviderId
  }
`

interface ChNeuropsychologicDiagnosticPrescriptionDrawerProps {
  progressNoteId: string
  id?: string
  neuropsychologicDiagnosticPrescriptionRef?: chNeuropsychologicDiagnosticPrescriptionDrawerFragment$key
}

const ChNeuropsychologicDiagnosticPrescriptionDrawer = withErrorBoundary(
  ({
    neuropsychologicDiagnosticPrescriptionRef,
    progressNoteId,
    id,
  }: ChNeuropsychologicDiagnosticPrescriptionDrawerProps) => {
    const { t } = useTranslation()
    const { toast } = useToast()
    const user = useUser()
    const hasSavePermission = user.isOneOf(["Doctor", "MedicalSupervisor"])
    const closeDrawer = useDrawerStore((state) => state.closeDrawer)
    const patientFileInfo = usePatientFileContext((state) => state.patientFileInfo)
    const [createNeuropsychologicDiagnosticPrescription, isCreating] =
      useChCreateNeuropsychologicDiagnosticPrescriptionMutation()
    const [updateNeuropsychologicDiagnosticPrescription, isUpdating] =
      useChUpdateNeuropsychologicDiagnosticPrescriptionMutation()
    const [deleteNeuropsychologicDiagnosticPrescription] = useChDeleteNeuropsychologicDiagnosticPrescriptionMutation()
    const [generateNeuropsychologicDiagnosticPrescription, isGenerating] =
      useChGenerateNeuropsychologicDiagnosticPrescriptionMutation()
    const patientId = usePatientParam()
    const patientAge = patientFileInfo?.age.years
    const isPatientOfAge = patientAge ? patientAge >= 18 : false
    const isEditNeuropsychologicDiagnosticPrescription = !!id
    const isAddNeuropsychologicDiagnosticPrescription =
      !isEditNeuropsychologicDiagnosticPrescription && patientId && progressNoteId
    const persistedData = useFragment(
      chNeuropsychologicDiagnosticPrescriptionFragment,
      neuropsychologicDiagnosticPrescriptionRef,
    )
    const contextAppointment = useContextAppointment()
    const appointmentLanguage = contextAppointment?.language
    const updatedAt = persistedData?.updatedAt ?? persistedData?.createdAt
    const updatedBy = persistedData?.updatedBy ?? persistedData?.createdBy

    const confirm = useConfirmStore((state) => state.confirm)
    const cancel = useConfirmStore((state) => state.cancel)

    const defaultLanguage: Language =
      DOCUMENT_LANGUAGES.find((lang) => lang.value === appointmentLanguage)?.value ?? "GERMAN"
    const parsedData = isEditNeuropsychologicDiagnosticPrescription
      ? NeuropsychologicDiagnosticPrescriptionBaseSchema.parse({
          ...persistedData,
          email: persistedData?.email ?? "",
          phoneNumber: persistedData?.phoneNumber ?? "",
          relationship: persistedData?.relationship ?? "",
          healthcareProviderId: persistedData?.healthcareProviderId ?? "",
        })
      : null

    const hasAccompanyingPersonData =
      parsedData?.name || parsedData?.phoneNumber || parsedData?.email || parsedData?.relationship

    const form = useForm<NeuropsychologicDiagnosticPrescription>({
      mode: "all",
      resolver: zodResolver(NeuropsychologicDiagnosticPrescriptionSchema),
      defaultValues:
        isEditNeuropsychologicDiagnosticPrescription && parsedData
          ? {
              ...parsedData,
            }
          : {
              language: defaultLanguage,
              medicalPrescriptions: [],
              phoneNumber: "",
              name: "",
              relationship: "",
            },
    })

    const {
      control,
      formState: { isDirty, errors, isValidating },
      register,
    } = form

    useDirtyDrawerMaker(isDirty)

    const handleCloseDrawer = () => {
      closeDrawer()
    }

    const onSubmit: SubmitHandler<NeuropsychologicDiagnosticPrescription> = (data) => {
      if (!progressNoteId) return

      const formattedData = {
        ...data,
        healthcareProviderId: data.healthcareProviderId || null,
      }

      if (isAddNeuropsychologicDiagnosticPrescription) {
        createNeuropsychologicDiagnosticPrescription({
          variables: {
            input: {
              patientId,
              progressNoteId,
              language: formattedData.language,
              healthcareProviderId: formattedData.healthcareProviderId,
              name: formattedData.name,
              phoneNumber: formattedData.phoneNumber,
              email: formattedData.email,
              relationship: formattedData.relationship,
              medicalPrescriptions: formattedData.medicalPrescriptions,
            },
          },
          onCompleted({ ch_createNeuropsychologicDiagnosticPrescription }) {
            const id = ch_createNeuropsychologicDiagnosticPrescription.ch_NeuropsychologicDiagnosticPrescription?.id
            if (id) {
              handleGenerateNeuropsychologicDiagnosticPrescription(id)
            }
          },
          updater(store, data) {
            try {
              const progressNoteRecordProxy = store.get(progressNoteId)
              const neuropsychologicDiagnosticPrescription =
                data?.ch_createNeuropsychologicDiagnosticPrescription.ch_NeuropsychologicDiagnosticPrescription
              if (neuropsychologicDiagnosticPrescription?.id) {
                const newRecordProxy = store.get(neuropsychologicDiagnosticPrescription.id)
                if (newRecordProxy) {
                  progressNoteRecordProxy?.setLinkedRecord(newRecordProxy, "ch_neuropsychologicDiagnosticPrescription")
                }
              }
            } catch (error) {
              console.error("Error while processing data:", error)
            }
          },
        })
      }

      if (isEditNeuropsychologicDiagnosticPrescription) {
        updateNeuropsychologicDiagnosticPrescription({
          variables: {
            input: {
              id,
              language: formattedData.language,
              healthcareProviderId: formattedData.healthcareProviderId,
              name: formattedData.name,
              phoneNumber: formattedData.phoneNumber,
              email: formattedData.email,
              relationship: formattedData.relationship,
              medicalPrescriptions: formattedData.medicalPrescriptions,
            },
          },
          onCompleted({ ch_updateNeuropsychologicDiagnosticPrescription }) {
            const id = ch_updateNeuropsychologicDiagnosticPrescription.ch_NeuropsychologicDiagnosticPrescription?.id
            if (id) {
              handleGenerateNeuropsychologicDiagnosticPrescription(id)
            }
          },
        })
      }
    }

    const onDelete = () => {
      if (progressNoteId && id) {
        deleteNeuropsychologicDiagnosticPrescription({
          variables: {
            input: {
              id,
            },
          },
          onCompleted() {
            handleCloseDrawer()
            toast({
              description: t("patient.neuropsychologicDiagnosticPrescription.deleteMessage.success"),
            })
            cancel()
          },
        })
      }
    }

    const handleDelete = () => {
      confirm({
        title: t("patient.neuropsychologicDiagnosticPrescription.deleteConfirmTitle"),
        content: t("episodeOfCare.deleteProgressNote.content"),
        onConfirm: onDelete,
        confirmVariant: "danger",
        confirmText: t("common.button.delete"),
        cancelText: t("common.button.keep"),
      })
    }

    const handleGenerateNeuropsychologicDiagnosticPrescription = (id: string) => {
      generateNeuropsychologicDiagnosticPrescription({
        id,
        onCompleted() {
          handleCloseDrawer()
        },
      })
    }

    return (
      <Drawer open onOpenChange={handleCloseDrawer}>
        <FormProvider {...form}>
          <DrawerContent>
            <DrawerClose className="top-2 right-2" />
            <DrawerHeader className="pb-4" data-testid="ch-neuropsychologic-diagnostic-prescription-header">
              <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium">
                {t("patient.neuropsychologicDiagnosticPrescription.neuropsychologicDiagnosticPrescription")}
              </DrawerTitle>
            </DrawerHeader>
            <DrawerBody data-testid="ch-neuropsychologic-diagnostic-prescription-body">
              <div className="mb-8 flex flex-col gap-4">
                <FormField
                  control={control}
                  name="language"
                  render={({
                    field: { name, onChange, value, ref },
                    formState: { errors },
                    fieldState: { invalid },
                  }) => (
                    <div>
                      <Select value={value} onValueChange={onChange} {...ref}>
                        <SelectTrigger hasError={invalid} className="w-56.5">
                          <SelectValue asChild>
                            <LabelAndSelectValue
                              data-testid="document-language"
                              label={t("patient.neuropsychologicDiagnosticPrescription.documentLanguage") + "*"}
                              value={t(getDocumentLanguageLabel(value))}
                            />
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {DOCUMENT_LANGUAGES.map((language) => (
                            <SelectItem
                              value={language.value}
                              key={language.value}
                              data-testid={`document-language-${language.value.toLocaleLowerCase()}`}>
                              {t(language.label)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <TranslatedFieldError errors={errors} name={name} />
                    </div>
                  )}
                />
              </div>
              <div className="mb-8 flex flex-col gap-4">
                <label className="text-on-surface text-base font-medium">{t("patient.therapyPrescription.to")}</label>
                <div className="w-full">
                  <FormField
                    name="healthcareProviderId"
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                      <CareProviderSearch
                        label={t("patient.therapyPrescription.healthcareProvider")}
                        searchType="THERAPY_PRESCRIPTION_NEUROPSYCHOLOGIC_DIAGNOSTIC_DOCUMENT"
                        name={name}
                        value={value}
                        analyticsSectionName="documents_neuroPsychology"
                        onSelect={onChange}
                        onRemove={() => onChange("")}
                        error={errors.healthcareProviderId?.message}
                      />
                    )}
                  />
                </div>
              </div>
              <div>
                <div
                  className={cn(
                    "mb-2",
                    errors.medicalPrescriptions?.message &&
                      "outline-critical! rounded-md outline-2 outline-offset-4 outline-solid",
                  )}>
                  <label className="text-on-surface-subdued block pb-2 text-sm">
                    {t("patient.neuropsychologicDiagnosticPrescription.medicalPrescription") + "*"}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {MedicalPrescriptionValues.map((medicalPrescription) => (
                      <FormField
                        key={medicalPrescription}
                        control={control}
                        name="medicalPrescriptions"
                        render={({ field: { value = [], onChange } }) => (
                          <DetailCheckbox
                            key={medicalPrescription}
                            checked={value.includes(medicalPrescription)}
                            data-testid={`${medicalPrescription.toLowerCase()}-medical-prescription-checkbox`}
                            onCheckedChange={(checked) => {
                              const checkedValues = checked
                                ? [...value, medicalPrescription].sort((a, b) => a.localeCompare(b))
                                : value.filter((value) => value !== medicalPrescription)

                              onChange(checkedValues)
                            }}>
                            {t(
                              `patient.neuropsychologicDiagnosticPrescription.medicalPrescriptionList.${medicalPrescription}`,
                            )}
                          </DetailCheckbox>
                        )}
                      />
                    ))}
                  </div>
                </div>
                <TranslatedFieldError errors={errors} name="medicalPrescriptions" />
              </div>
              <div className={cn("pt-8", !isPatientOfAge && "[&>div]:-mx-0.5")}>
                <Accordion
                  type="single"
                  collapsible={isPatientOfAge}
                  defaultValue={!isPatientOfAge || hasAccompanyingPersonData ? "accompanying-person" : undefined}
                  className={cn(!isPatientOfAge && "rounded-none border-none")}>
                  <AccordionItem value="accompanying-person">
                    <AccordionTrigger
                      className={cn(
                        "px-4 select-text hover:bg-transparent active:bg-transparent",
                        !isPatientOfAge && "cursor-auto p-0 [&>svg]:hidden",
                      )}>
                      <label
                        className={cn(
                          "text-on-surface w-full text-base font-medium",
                          isPatientOfAge && "pointer-events-none",
                        )}>
                        {t("patient.neuropsychologicDiagnosticPrescription.accompanyingPerson")}
                      </label>
                    </AccordionTrigger>
                    <AccordionContent className={cn("space-y-4 overflow-visible", !isPatientOfAge && "mt-2 px-0.5")}>
                      <Input
                        {...register("name")}
                        label={t("patient.neuropsychologicDiagnosticPrescription.lastNameOrGivenName")}
                        error={errors.name?.message}
                        translateError
                        errorClasses="truncate"
                        data-testid="last-name-or-given-name-input"
                      />
                      <FormField
                        name="phoneNumber"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <PhoneNumberInput
                            label={t("common.phoneNumber")}
                            value={value}
                            onChange={onChange}
                            error={t(errors.phoneNumber?.message ?? "")}
                            data-testid="phone-number-input"
                            isSMSSupported
                          />
                        )}
                      />
                      <FormField
                        name="email"
                        control={control}
                        render={({ field: { value, ...rest } }) => {
                          return (
                            <Input
                              data-testid="email-input"
                              label={t("common.email")}
                              error={errors.email?.message}
                              translateError
                              value={value ?? ""}
                              {...rest}
                            />
                          )
                        }}
                      />
                      <Input
                        {...register("relationship")}
                        label={t("patient.neuropsychologicDiagnosticPrescription.relationToPatient")}
                        error={errors.relationship?.message}
                        translateError
                        errorClasses="truncate"
                        data-testid="relation-to-patient-input"
                      />
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </DrawerBody>
            <DrawerFooter className="flex-wrap gap-2" data-testid="ch-neuropsychologic-diagnostic-prescription-footer">
              {isEditNeuropsychologicDiagnosticPrescription && (
                <div className="flex gap-2">
                  {updatedAt && updatedBy && <LastModificationInfo updatedAt={updatedAt} updatedBy={updatedBy} />}
                  <button
                    onClick={handleDelete}
                    data-testid="ch-neuropsychologic-diagnostic-prescription-delete-button">
                    <TrashSimple className="text-on-surface-subdued" />
                  </button>
                </div>
              )}
              <div className="ml-auto flex gap-2">
                <Button
                  data-testid="ch-neuropsychologic-diagnostic-prescription-cancel-button"
                  variant="secondary"
                  onClick={handleCloseDrawer}>
                  {t("common.button.cancel")}
                </Button>
                <Button
                  disabled={
                    !hasSavePermission || isValidating || (isEditNeuropsychologicDiagnosticPrescription && !isDirty)
                  }
                  data-testid="ch-neuropsychologic-diagnostic-prescription-save-button"
                  isLoading={isCreating || isUpdating || isGenerating}
                  onClick={() => {
                    FaroInsightsWrapper.pushEvent("documents_neuroPsychology_save")
                    form.handleSubmit(onSubmit)()
                  }}>
                  {t("common.button.save")}
                </Button>
              </div>
            </DrawerFooter>
          </DrawerContent>
        </FormProvider>
      </Drawer>
    )
  },
  "ChNeuropsychologicDiagnosticPrescriptionDrawer",
)

export { ChNeuropsychologicDiagnosticPrescriptionDrawer }
